import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, RefObject, TextareaHTMLAttributes } from 'react'
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { StatusTone } from '@orbind/tokens'

const cx = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(' ')

// ------------------------------------------------------------------ Button

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant
  size?: 'md' | 'sm'
  loading?: boolean
  icon?: string
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  icon,
  children,
  className,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      className={cx('ob-btn', `ob-btn--${variant}`, size === 'sm' && 'ob-btn--sm', className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <span className="ob-btn__spinner" aria-hidden /> : icon ? <i className={icon} aria-hidden /> : null}
      {children}
    </button>
  )
}

// -------------------------------------------------------------- IconButton

/**
 * Botao apenas com icone. `label` e obrigatorio: vira `aria-label` e `title`,
 * porque um botao sem texto acessivel e invisivel para leitor de tela (spec 6).
 */
export type IconButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
  icon: string
  label: string
  size?: 'md' | 'sm'
}

export function IconButton({ icon, label, size = 'md', className, ...rest }: IconButtonProps) {
  return (
    <button
      type="button"
      className={cx('ob-icon-btn', size === 'sm' && 'ob-icon-btn--sm', className)}
      aria-label={label}
      title={label}
      {...rest}
    >
      <i className={icon} aria-hidden />
    </button>
  )
}

// ------------------------------------------------------------------- Input

export type InputProps = InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }

export function Input({ invalid, className, type, ...rest }: InputProps) {
  // Campo de senha ganha o botao de mostrar/ocultar (olho), como nas apps
  // comuns. Vale para login, redefinir senha, cadastro de membro, etc.
  const [revealed, setRevealed] = useState(false)
  if (type === 'password') {
    return (
      <span className="ob-inputwrap">
        <input
          className={cx('ob-input', 'ob-input--haseye', invalid && 'ob-input--invalid', className)}
          aria-invalid={invalid || undefined}
          type={revealed ? 'text' : 'password'}
          {...rest}
        />
        <button
          type="button"
          className="ob-input-reveal"
          aria-label={revealed ? 'Ocultar senha' : 'Mostrar senha'}
          aria-pressed={revealed}
          title={revealed ? 'Ocultar senha' : 'Mostrar senha'}
          // fora da ordem de tabulacao: nao atrapalha o fluxo do formulario
          tabIndex={-1}
          onClick={() => setRevealed((v) => !v)}
        >
          <i className={revealed ? 'fas fa-eye-slash' : 'fas fa-eye'} aria-hidden />
        </button>
      </span>
    )
  }
  return (
    <input
      className={cx('ob-input', invalid && 'ob-input--invalid', className)}
      aria-invalid={invalid || undefined}
      type={type}
      {...rest}
    />
  )
}

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }

export function Textarea({ invalid, className, ...rest }: TextareaProps) {
  return (
    <textarea
      className={cx('ob-input', invalid && 'ob-input--invalid', className)}
      aria-invalid={invalid || undefined}
      {...rest}
    />
  )
}

// ---------------------------------------------------------------- Checkbox

export type CheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { label: ReactNode }

export function Checkbox({ label, className, ...rest }: CheckboxProps) {
  return (
    <label className={cx('ob-check', className)}>
      <input type="checkbox" {...rest} />
      <span className="ob-check__box" aria-hidden>
        <i className="fas fa-check" />
      </span>
      <span>{label}</span>
    </label>
  )
}

// ------------------------------------------------------------------- Badge

export type BadgeProps = {
  tone?: StatusTone
  icon?: string
  children: ReactNode
  className?: string
}

/**
 * Badge de status.
 *
 * O `icon` nao e decoracao opcional: a spec 6 exige que status nunca seja
 * comunicado apenas por cor. Use icone ou texto explicito junto.
 */
export function Badge({ tone = 'neutral', icon, children, className }: BadgeProps) {
  return (
    <span className={cx('ob-badge', `ob-badge--${tone}`, className)}>
      {icon ? <i className={icon} aria-hidden /> : null}
      {children}
    </span>
  )
}

// -------------------------------------------------------------------- Card

export type CardProps = {
  children: ReactNode
  /** `list` remove o chrome no mobile, como manda a spec 6. */
  variant?: 'default' | 'flush' | 'list'
  className?: string
}

export function Card({ children, variant = 'default', className }: CardProps) {
  return (
    <div className={cx('ob-card', variant !== 'default' && `ob-card--${variant}`, className)}>{children}</div>
  )
}

// ---------------------------------------------------------- popover portal

/**
 * Posicao fixa (viewport) da lista de um combo, ancorada no gatilho.
 *
 * A lista renderiza em portal no body: dentro de Modal/Drawer o corpo tem
 * overflow e RECORTA um popover absoluto — o portal fica sobre a janela toda.
 */
export function usePopoverRect(open: boolean, anchorRef: RefObject<HTMLElement | null>) {
  const [rect, setRect] = useState<{ top: number; left: number; width: number } | null>(null)

  useLayoutEffect(() => {
    if (!open) {
      setRect(null)
      return
    }
    const update = () => {
      const r = anchorRef.current?.getBoundingClientRect()
      if (r) setRect({ top: r.bottom + 4, left: r.left, width: r.width })
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [open, anchorRef])

  return rect
}

// ------------------------------------------------------------------ Select

export type SelectOption<T extends string = string> = {
  value: T
  label: string
  icon?: string
  tone?: StatusTone
  disabled?: boolean
  /**
   * Grupo da opção (SPEC-20261103-2117): opções com o mesmo `group`, em
   * sequência, ganham um cabeçalho na lista — "fluxo › etapas dele".
   */
  group?: string
}

/** Cabeçalho de grupo antes da opção `index`, quando o grupo muda. */
function cabecalhoDeGrupo(options: readonly { group?: string }[], index: number) {
  const g = options[index]?.group
  if (!g || (index > 0 && options[index - 1]?.group === g)) return null
  return (
    <li key={`g:${g}:${index}`} className="ob-select__group" role="presentation">
      {g}
    </li>
  )
}

export type SelectProps<T extends string = string> = {
  value: T | null
  options: readonly SelectOption<T>[]
  onChange: (value: T) => void
  placeholder?: string
  disabled?: boolean
  invalid?: boolean
  id?: string
  'aria-labelledby'?: string
}

/**
 * Select em popover.
 *
 * `<select>` nativo e proibido pela spec 5.4 — ele nao aceita icone, cor de
 * tone nem o visual do design system. Em troca, a navegacao por teclado
 * (setas, Home/End, Enter, Esc) precisa ser implementada aqui.
 */
export function Select<T extends string = string>({
  value,
  options,
  onChange,
  placeholder = 'Selecione',
  disabled,
  invalid,
  id,
  ...aria
}: SelectProps<T>) {
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)
  const popRef = useRef<HTMLDivElement>(null)
  const listId = useId()
  const popRect = usePopoverRect(open, rootRef)

  const selected = useMemo(() => options.find((o) => o.value === value) ?? null, [options, value])
  const enabledIndexes = useMemo(
    () => options.map((o, i) => (o.disabled ? -1 : i)).filter((i) => i >= 0),
    [options],
  )

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (!rootRef.current?.contains(target) && !popRef.current?.contains(target)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  useEffect(() => {
    if (open) setActiveIndex(Math.max(0, options.findIndex((o) => o.value === value)))
  }, [open, options, value])

  const move = (delta: number) => {
    if (enabledIndexes.length === 0) return
    const current = enabledIndexes.indexOf(activeIndex)
    const next = enabledIndexes[(current + delta + enabledIndexes.length) % enabledIndexes.length]
    if (next !== undefined) setActiveIndex(next)
  }

  const commit = (index: number) => {
    const option = options[index]
    if (!option || option.disabled) return
    onChange(option.value)
    setOpen(false)
  }

  return (
    <div className="ob-select" ref={rootRef}>
      <button
        type="button"
        id={id}
        className={cx('ob-select__trigger', invalid && 'ob-select__trigger--invalid')}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        {...aria}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            if (!open) setOpen(true)
            else commit(activeIndex)
          } else if (e.key === 'Escape') {
            setOpen(false)
          } else if (open && e.key === 'ArrowUp') {
            e.preventDefault()
            move(-1)
          } else if (open && e.key === 'ArrowDown') {
            e.preventDefault()
            move(1)
          }
        }}
      >
        <span className={cx('ob-grow', !selected && 'ob-select__value--placeholder')}>
          {selected ? (
            <span className="ob-inline">
              {selected.icon ? <i className={selected.icon} aria-hidden /> : null}
              {selected.label}
            </span>
          ) : (
            placeholder
          )}
        </span>
        <i className="fas fa-chevron-down" aria-hidden style={{ fontSize: 10, color: 'var(--ob-text-muted)' }} />
      </button>

      {open && popRect
        ? createPortal(
            <div
              ref={popRef}
              className="ob-select__popover ob-select__popover--portal"
              style={{ top: popRect.top, left: popRect.left, minWidth: popRect.width }}
            >
              <ul className="ob-select__list" id={listId} role="listbox" tabIndex={-1}>
                {options.flatMap((option, index) => [
                  cabecalhoDeGrupo(options, index),
                  <li
                    key={option.value}
                    role="option"
                    aria-selected={option.value === value}
                    aria-disabled={option.disabled || undefined}
                    className={cx('ob-select__option', index === activeIndex && 'ob-select__option--active', option.group && 'ob-select__option--agrupada')}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => commit(index)}
                  >
                    {option.icon ? <i className={option.icon} aria-hidden /> : null}
                    {option.label}
                  </li>,
                ])}
              </ul>
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}

// ---------------------------------------------------------------- TagSelect

export type TagSelectProps<T extends string = string> = {
  value: readonly T[]
  options: readonly SelectOption<T>[]
  onChange: (value: T[]) => void
  placeholder?: string
  disabled?: boolean
  invalid?: boolean
  id?: string
  emptyLabel?: string
}

const stripAccents = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

/**
 * Campo de tags com busca: digita dentro, as opcoes aparecem filtradas, e a
 * selecionada vira uma tag com "x" para remover. Backspace com o campo vazio
 * remove a ultima tag; setas + Enter navegam a lista.
 */
export function TagSelect<T extends string = string>({
  value,
  options,
  onChange,
  placeholder = 'Digite para buscar…',
  disabled,
  invalid,
  id,
  emptyLabel = 'Nenhuma opção encontrada',
}: TagSelectProps<T>) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)
  const popRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const popRect = usePopoverRect(open, rootRef)

  const selected = useMemo(
    () => value.map((v) => options.find((o) => o.value === v)).filter((o): o is SelectOption<T> => Boolean(o)),
    [value, options],
  )
  const available = useMemo(
    () => options.filter((o) => !value.includes(o.value) && stripAccents(o.label).includes(stripAccents(query))),
    [options, value, query],
  )

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (!rootRef.current?.contains(target) && !popRef.current?.contains(target)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  useEffect(() => setActiveIndex(0), [query, open])

  const add = (v: T) => {
    onChange([...value, v])
    setQuery('')
    inputRef.current?.focus()
  }
  const remove = (v: T) => {
    onChange(value.filter((x) => x !== v))
    inputRef.current?.focus()
  }

  return (
    <div
      ref={rootRef}
      className={cx('ob-tagselect', invalid && 'ob-tagselect--invalid', disabled && 'ob-tagselect--disabled')}
    >
      <div
        className="ob-tagselect__box"
        onClick={() => {
          if (disabled) return
          inputRef.current?.focus()
          setOpen(true)
        }}
      >
        {selected.map((option) => (
          <span key={option.value} className="ob-tag">
            {option.icon ? <i className={option.icon} aria-hidden /> : null}
            {option.label}
            <button
              type="button"
              className="ob-tag__remove"
              aria-label={`Remover ${option.label}`}
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation()
                remove(option.value)
              }}
            >
              <i className="fas fa-times" aria-hidden />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          id={id}
          className="ob-tagselect__input"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          value={query}
          placeholder={selected.length === 0 ? placeholder : ''}
          disabled={disabled}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && query === '' && value.length > 0) {
              onChange(value.slice(0, -1))
            } else if (e.key === 'ArrowDown') {
              e.preventDefault()
              setOpen(true)
              setActiveIndex((i) => Math.min(i + 1, Math.max(available.length - 1, 0)))
            } else if (e.key === 'ArrowUp') {
              e.preventDefault()
              setActiveIndex((i) => Math.max(i - 1, 0))
            } else if (e.key === 'Enter') {
              e.preventDefault()
              const option = available[activeIndex]
              if (open && option) add(option.value)
            } else if (e.key === 'Escape') {
              setOpen(false)
            }
          }}
        />
      </div>

      {open && !disabled && popRect
        ? createPortal(
            <div
              ref={popRef}
              className="ob-select__popover ob-select__popover--portal"
              style={{ top: popRect.top, left: popRect.left, minWidth: popRect.width }}
            >
              <ul className="ob-select__list" role="listbox" tabIndex={-1}>
                {available.length === 0 ? (
                  <li className="ob-tagselect__empty">{emptyLabel}</li>
                ) : (
                  available.flatMap((option, index) => [
                    cabecalhoDeGrupo(available, index),
                    <li
                      key={option.value}
                      role="option"
                      aria-selected={false}
                      className={cx('ob-select__option', index === activeIndex && 'ob-select__option--active', option.group && 'ob-select__option--agrupada')}
                      onMouseEnter={() => setActiveIndex(index)}
                      // preventDefault: o clique na opcao nao pode tirar o foco do input
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => add(option.value)}
                    >
                      {option.icon ? <i className={option.icon} aria-hidden /> : null}
                      {option.label}
                    </li>,
                  ])
                )}
              </ul>
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}

// ---------------------------------------------------------------- DateInput

const DOW_PT_BR = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb']

const formatPtBR = (date: Date) =>
  new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date)

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

export type DateInputProps = {
  value: Date | null
  onChange: (value: Date | null) => void
  placeholder?: string
  disabled?: boolean
  invalid?: boolean
  clearable?: boolean
  id?: string
}

/**
 * Campo de data com calendario proprio em pt-BR.
 *
 * `<input type="date">` e proibido pela spec 5.4: o widget nativo muda de
 * aparencia e de formato conforme o SO e o idioma do navegador, o que quebra a
 * consistencia visual e faz o usuario brasileiro ver mm/dd/yyyy em maquina
 * configurada em ingles.
 */
export function DateInput({
  value,
  onChange,
  placeholder = 'dd/mm/aaaa',
  disabled,
  invalid,
  clearable = true,
  id,
}: DateInputProps) {
  const [open, setOpen] = useState(false)
  const [cursor, setCursor] = useState(() => value ?? new Date())
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  useEffect(() => {
    if (value) setCursor(value)
  }, [value])

  // Grade de 6 semanas: mantem a altura do popover estavel entre os meses.
  const days = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1)
    const start = new Date(first)
    start.setDate(first.getDate() - first.getDay())
    return Array.from({ length: 42 }, (_, i) => {
      const day = new Date(start)
      day.setDate(start.getDate() + i)
      return day
    })
  }, [cursor])

  const today = new Date()
  const monthLabel = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(cursor)

  return (
    <div className="ob-date" ref={rootRef}>
      <button
        type="button"
        id={id}
        className={cx('ob-date__trigger', invalid && 'ob-date__trigger--invalid')}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={cx('ob-grow', !value && 'ob-select__value--placeholder')} style={{ textAlign: 'left' }}>
          {value ? formatPtBR(value) : placeholder}
        </span>
        {value && clearable ? (
          <i
            className="fas fa-times"
            aria-hidden
            style={{ fontSize: 11, color: 'var(--ob-text-muted)' }}
            onClick={(e) => {
              e.stopPropagation()
              onChange(null)
            }}
          />
        ) : (
          <i className="far fa-calendar" aria-hidden style={{ fontSize: 12, color: 'var(--ob-text-muted)' }} />
        )}
      </button>

      {open ? (
        <div className="ob-date__popover" role="dialog" aria-label="Selecionar data">
          <div className="ob-date__header">
            <IconButton
              icon="fas fa-chevron-left"
              label="Mês anterior"
              size="sm"
              onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
            />
            <span>{monthLabel}</span>
            <IconButton
              icon="fas fa-chevron-right"
              label="Próximo mês"
              size="sm"
              onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
            />
          </div>
          <div className="ob-date__grid">
            {DOW_PT_BR.map((dow) => (
              <div key={dow} className="ob-date__dow">
                {dow}
              </div>
            ))}
            {days.map((day) => (
              <button
                key={day.toISOString()}
                type="button"
                className={cx(
                  'ob-date__day',
                  day.getMonth() !== cursor.getMonth() && 'ob-date__day--outside',
                  sameDay(day, today) && 'ob-date__day--today',
                  value && sameDay(day, value) && 'ob-date__day--selected',
                )}
                aria-label={formatPtBR(day)}
                aria-current={sameDay(day, today) ? 'date' : undefined}
                onClick={() => {
                  onChange(day)
                  setOpen(false)
                }}
              >
                {day.getDate()}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}

export { cx }
